import React, { useState, useEffect } from 'react';
import { supportAPI, boardsAPI } from '../api';

/**
 * ContactMaestraModal — minimal, editorial. A single textarea with optional
 * category + board selector. No support-software vibes. No ticket numbers,
 * no SLA promises, no "how can I help you today?" greetings.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - t: translation map (already resolved by parent)
 *   - currentBoardId: optional pre-selection
 */
const CATEGORY_OPTIONS = [
  { value: 'workspace_help', key: 'contact_cat_workspace' },
  { value: 'board_recovery', key: 'contact_cat_board_recovery' },
  { value: 'technical',      key: 'contact_cat_technical' },
  { value: 'other',          key: 'contact_cat_other' },
];

const ContactMaestraModal = ({ open, onClose, t, currentBoardId = null }) => {
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('other');
  const [boardId, setBoardId] = useState(currentBoardId || '');
  const [boards, setBoards] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    boardsAPI.list()
      .then(res => setBoards((res.data || []).filter(b => !b.is_demo)))
      .catch(() => setBoards([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      // Reset state when opened fresh
      setMessage('');
      setCategory('other');
      setBoardId(currentBoardId || '');
      setSent(false);
      setError('');
    }
  }, [open, currentBoardId]);

  if (!open) return null;

  const submit = async () => {
    if (message.trim().length < 5) {
      setError(t.contact_min_message || 'Please write at least a few words.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await supportAPI.contact({
        message: message.trim(),
        category,
        board_id: boardId || null,
      });
      setSent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || t.contact_send_failed || 'Could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="contact-modal-overlay"
      onClick={onClose}
      style={overlayStyle}
    >
      <div
        data-testid="contact-modal"
        onClick={(e) => e.stopPropagation()}
        style={modalStyle}
      >
        {sent ? (
          <ThankYou t={t} onClose={onClose} />
        ) : (
          <>
            <header style={{ marginBottom: 28 }}>
              <p style={kicker}>{t.studio_name || 'Maestra'}</p>
              <h2 style={titleStyle}>
                {t.contact_modal_title || 'Write to Maestra.'}
              </h2>
              <p style={italicCopy}>
                {t.contact_modal_subtitle ||
                  'A direct line to the founder. Replies usually arrive within a day or two.'}
              </p>
            </header>

            <label style={fieldLabel} htmlFor="contact-message">
              {t.contact_field_message || 'Your message'}
            </label>
            <textarea
              id="contact-message"
              data-testid="contact-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
              rows={6}
              placeholder={t.contact_placeholder || ''}
              style={textareaStyle}
            />

            <div style={twoCol}>
              <div>
                <label style={fieldLabel} htmlFor="contact-category">
                  {t.contact_field_category || 'About'}
                </label>
                <select
                  id="contact-category"
                  data-testid="contact-category-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  style={selectStyle}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t[opt.key] || opt.value}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={fieldLabel} htmlFor="contact-board">
                  {t.contact_field_board || 'Board (optional)'}
                </label>
                <select
                  id="contact-board"
                  data-testid="contact-board-select"
                  value={boardId}
                  onChange={(e) => setBoardId(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">— {t.contact_board_none || 'None'} —</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name || 'Untitled'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <p data-testid="contact-error" style={errorStyle}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: 14, justifyContent: 'flex-end', marginTop: 26 }}>
              <button
                type="button"
                data-testid="contact-cancel-btn"
                onClick={onClose}
                style={ghostBtn(submitting)}
              >
                {t.cancel || 'Cancel'}
              </button>
              <button
                type="button"
                data-testid="contact-send-btn"
                onClick={submit}
                disabled={submitting || message.trim().length < 5}
                style={primaryBtn(submitting || message.trim().length < 5)}
              >
                {submitting
                  ? (t.contact_sending || 'Sending…')
                  : (t.contact_send || 'Send message')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ThankYou = ({ t, onClose }) => (
  <div data-testid="contact-sent-screen" style={{ textAlign: 'center', padding: '20px 8px' }}>
    <p style={kicker}>{t.studio_name || 'Maestra'}</p>
    <h3 style={{ ...titleStyle, fontSize: 22 }}>
      {t.contact_sent_title || 'Your message is on its way.'}
    </h3>
    <p style={italicCopy}>
      {t.contact_sent_body ||
        'I read every message personally. You\'ll hear back soon, usually within a day or two.'}
    </p>
    <button
      type="button"
      data-testid="contact-sent-close-btn"
      onClick={onClose}
      style={{ ...primaryBtn(false), marginTop: 28 }}
    >
      {t.close || 'Close'}
    </button>
  </div>
);

// ============================================================
// Styles — match the soft-delete confirmation modal aesthetic
// ============================================================
const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(20, 18, 14, 0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999, padding: 20,
};
const modalStyle = {
  background: '#fff',
  padding: '44px 48px 40px',
  maxWidth: 540, width: '100%',
  border: '1px solid var(--kahla-border, #e5e1d8)',
  maxHeight: '90vh',
  overflowY: 'auto',
};
const kicker = {
  fontFamily: 'Playfair Display, serif',
  fontSize: 11, letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: 'var(--kahla-text-light, #8a847b)',
  margin: '0 0 14px', fontWeight: 300,
};
const titleStyle = {
  fontFamily: 'Playfair Display, serif',
  fontSize: 26, fontWeight: 400, lineHeight: 1.3,
  color: 'var(--kahla-charcoal, #4a4540)',
  margin: '0 0 12px',
};
const italicCopy = {
  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic',
  fontSize: 16, lineHeight: 1.6,
  color: 'var(--kahla-text-light, #8a847b)',
  margin: 0,
};
const fieldLabel = {
  display: 'block',
  fontFamily: 'Playfair Display, serif',
  fontSize: 10, letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: 'var(--kahla-text-light, #8a847b)',
  margin: '0 0 8px',
};
const textareaStyle = {
  width: '100%',
  padding: '14px 16px',
  fontFamily: 'Playfair Display, serif',
  fontSize: 14, lineHeight: 1.65,
  color: 'var(--kahla-charcoal, #4a4540)',
  border: '1px solid var(--kahla-border, #e5e1d8)',
  background: '#fdfcf9',
  boxSizing: 'border-box',
  resize: 'vertical',
  marginBottom: 22,
  outline: 'none',
};
const selectStyle = {
  width: '100%',
  padding: '10px 14px',
  fontFamily: 'Playfair Display, serif',
  fontSize: 13,
  color: 'var(--kahla-charcoal, #4a4540)',
  border: '1px solid var(--kahla-border, #e5e1d8)',
  background: '#fdfcf9',
  boxSizing: 'border-box',
  outline: 'none',
};
const twoCol = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 22,
};
const errorStyle = {
  background: '#f8eee9', color: '#7a3a3a',
  padding: '10px 14px',
  fontSize: 13, fontStyle: 'italic',
  margin: '18px 0 0',
};
const primaryBtn = (disabled) => ({
  background: 'var(--kahla-charcoal, #4a4540)', color: '#fff',
  border: 'none', padding: '11px 26px',
  fontFamily: 'inherit', fontSize: 11,
  letterSpacing: '0.18em', textTransform: 'uppercase',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});
const ghostBtn = (disabled) => ({
  background: 'transparent', color: 'var(--kahla-charcoal, #4a4540)',
  border: '1px solid var(--kahla-border, #e5e1d8)',
  padding: '11px 22px',
  fontFamily: 'inherit', fontSize: 11,
  letterSpacing: '0.18em', textTransform: 'uppercase',
  cursor: disabled ? 'wait' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

export default ContactMaestraModal;
